import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Callout } from '@liveauctioneers/hammer-ui-core/callout';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, BodySecondary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { useEffect, useState } from 'react';

import { PaymentMethodSelection } from '@/components/PaymentMethodSelection/PaymentMethodSelection';
import { PaymentProviders } from '@/enums';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    fetchBidderCreditCardsIfNeeded,
    updateBidderCreditCard,
} from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.actions';
import {
    getAllowedToAddCards,
    getCreditCardError,
    getLAPaymentCardsLoaded,
    laPaymentCardsForProviderSelector,
} from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import { openAddCreditCardModal } from '@/redux/modules/modal';
import { CreditCard } from '@/types/CreditCard';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { PaysafeModal } from './PaysafeModal';

export const CardManagement = () => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();

    const [cardAddError, setCardAddError] = useState(undefined);
    const [cardUpdateError, setCardUpdateError] = useState(undefined);
    const [showModal, setShowModal] = useState<boolean>(false);
    const allowedToAddCards = useAppSelector(getAllowedToAddCards);
    const laPaymentCardLoaded = useAppSelector(getLAPaymentCardsLoaded);
    const creditCardError = useAppSelector(getCreditCardError);
    const payrixCards: CreditCard[] = useAppSelector((state) =>
        laPaymentCardsForProviderSelector(state, PaymentProviders.Payrix)
    );
    // high risk can be paysafe and/or payrix.
    const highRiskCards: CreditCard[] = useAppSelector((state) =>
        laPaymentCardsForProviderSelector(state, PaymentProviders.Payrix)
    );
    useEffect(() => {
        if (!laPaymentCardLoaded) {
            dispatch(fetchBidderCreditCardsIfNeeded());
        }
    }, [dispatch, laPaymentCardLoaded]);

    useEffect(() => {
        if (
            creditCardError === "Can't remove card on file because there are unpaid items" ||
            creditCardError === "Can't remove card on file because there are pending absentee bids"
        ) {
            setCardUpdateError(formatMessage(['accountSettings', 'cannotRemoveCreditCard']));
        } else if (creditCardError === "Can't remove card on file because there are unpaid items or open disputes") {
            setCardUpdateError(formatMessage(['accountSettings', 'cannotRemoveCreditCardOpenDisputes']));
        }
    }, [creditCardError, formatMessage]);

    const onCardAdd = (provider: PaymentProviders) => {
        if (allowedToAddCards) {
            setCardAddError(undefined);
            dispatch(openAddCreditCardModal(provider));
        } else {
            setCardAddError(formatMessage(['creditCards', 'notAllowedToAddCards']));
        }
    };

    const onPayrixCardAdd = () => {
        onCardAdd(PaymentProviders.Payrix);
    };

    const onDeleteCard = (cardId: number) => {
        dispatch(
            updateBidderCreditCard({
                cardId,
                defaultCard: false,
                enabled: false,
            })
        );
    };

    const onEditCard = (card: CreditCard) => {
        dispatch(
            updateBidderCreditCard({
                cardId: card.id,
                defaultCard: true,
                enabled: true,
            })
        );
    };

    const openModalText = (
        <Link
            key="account-settings-high-risk-open-modal"
            onClick={() => setShowModal(true)}
        >
            {formatMessage(['accountSettings', 'openModal'])}
        </Link>
    );

    return (
        <>
            <H2 className="m-0">{formatMessage(['accountSettings', 'payments'])}</H2>
            {cardUpdateError && (
                <Callout
                    message={
                        <>
                            {cardUpdateError}
                            <div className="mt-xs flex gap-sm">
                                <Link href="https://help.liveauctioneers.com/article/134-how-do-i-pay-for-an-item">
                                    {formatMessage(['learn_more'])}
                                </Link>
                                <Link href="mailto:info@liveauctioneers.com">{formatMessage(['contact_us'])}</Link>
                            </div>
                        </>
                    }
                    onClose={() => {
                        setCardUpdateError(undefined);
                    }}
                    status="critical"
                />
            )}
            {payrixCards?.length > 0 ? (
                <PaymentMethodSelection
                    creditCards={payrixCards}
                    deleteCard={onDeleteCard}
                    selectCard={onEditCard}
                />
            ) : (
                <BodySecondary className="mx-0 my-md">
                    {formatMessage(['accountSettings', 'noCardOnFile'])}
                </BodySecondary>
            )}
            {highRiskCards?.length > 0 ? (
                <PaymentMethodSelection
                    creditCards={highRiskCards}
                    deleteCard={onDeleteCard}
                    selectCard={onEditCard}
                    title={formatMessage(['accountSettings', 'highRiskCofLabel'], {
                        values: { openModal: openModalText },
                    })}
                />
            ) : null}
            <PaysafeModal
                setShowModal={setShowModal}
                showModal={showModal}
            />

            <Button
                className="max-w-[500px]:w-full mb-sm ml-0 mr-xs mt-lg w-[194px]"
                data-testid="accountSettingsAddCardButton"
                onClick={onPayrixCardAdd}
            >
                {formatMessage(['accountSettings', 'addCardOnFileButton'])}
            </Button>

            {Boolean(cardAddError) && <BodyPrimary color="critical">{cardAddError}</BodyPrimary>}
        </>
    );
};
